import React, { useState } from 'react';
import { LabTest } from '../../services/api/LaborderService/types';

interface BasicInfoSectionProps {
  formData: LabTest;
  onInputChange: (field: keyof LabTest, value: string | boolean) => void;
}

const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  formData,
  onInputChange
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleValidation = (field: keyof LabTest, value: string) => {
    let errorMsg = '';

    switch (field) {
      case 'name':
        if (!value.trim()) errorMsg = 'Lab test name is required';
        break;
      case 'description':
        if (!value.trim()) errorMsg = 'Description is required';
        break;
      case 'price':
      case 'offerPrice':
      case 'discount':
        if (!value.trim()) errorMsg = 'This field is required';
        else if (isNaN(Number(value))) errorMsg = 'Must be a number';
        break;
      case 'reportTime':
        if (!value.trim()) errorMsg = 'Report time is required';
        break;
      case 'gender':
        if (!value.trim()) errorMsg = 'Please select gender';
        break;
      case 'sampleType':
        if (!value.trim()) errorMsg = 'Please select sample type';
        break;
      case 'fasting':
        if (value === '') errorMsg = 'Please select fasting requirement';
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [field]: errorMsg }));

    // Convert fasting to boolean
    if (field === 'fasting') {
      onInputChange('fasting', value === 'true');
    } else {
      onInputChange(field, value);
    }
  };

  return (
    <div className="w-full grid grid-cols-3 gap-6">
      {/* Lab Test Name */}
      <div className="col-span-3">
        <label className="block text-sm font-medium text-gray-700 mb-2">Lab Test Name</label>
        <input
          type="text"
          placeholder="Enter product name"
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          value={formData.name}
          onChange={(e) => handleValidation('name', e.target.value)}
        />
        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
      </div>

      {/* Description */}
      <div className="col-span-3">
        <label className="block text-sm font-medium text-gray-700 mb-2">Lab Test Description</label>
        <textarea
          placeholder="Enter Product description"
          rows={3}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          value={formData.description}
          onChange={(e) => handleValidation('description', e.target.value)}
        />
        {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
      </div>

      <div className="col-span-3">
        <label className="block text-sm font-medium text-gray-700 mb-2">Lab Test Other Names</label>
        <textarea
          placeholder="Enter Other Names"
          rows={3}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          value={formData.otherNames}
          onChange={(e) => handleValidation('otherNames', e.target.value)}
        />
        {errors.otherNames && <p className="text-red-500 text-xs mt-1">{errors.otherNames}</p>}
      </div>
      {/* Price Fields */}
      {['price', 'offerPrice', 'discount', 'reportTime'].map((field) => (
        <div key={field}>
          <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
            {field === 'reportTime'
              ? 'Report Time'
              : field === 'offerPrice'
                ? 'Offer Price'
                : field === 'discount'
                  ? 'Discount %'
                  : 'Lab Test Price'}
          </label>
          <input
            type="text"
            placeholder={`Enter ${field}`}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            value={(formData as any)[field]}
            onChange={(e) => handleValidation(field as keyof LabTest, e.target.value)}
          />
          {errors[field] && <p className="text-red-500 text-xs mt-1">{errors[field]}</p>}
        </div>
      ))}

      {/* Age fields — EXCLUDED from validation */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Min Age Limit</label>
        <input
          type="text"
          placeholder="Enter Age"
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          value={formData.ageLimitMin}
          onChange={(e) => onInputChange('ageLimitMin', e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Max Age Limit</label>
        <input
          type="text"
          placeholder="Enter Age"
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          value={formData.ageLimitMax}
          onChange={(e) => onInputChange('ageLimitMax', e.target.value)}
        />
      </div>

      {/* Dropdown fields */}
      {['gender', 'sampleType', 'fasting'].map((field) => (
        <div key={field}>
          <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
            {field === 'sampleType'
              ? 'Sample Type'
              : field === 'fasting'
                ? 'Fasting Required'
                : 'Gender'}
          </label>
          <select
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            value={
              field === 'fasting'
                ? String(formData.fasting)
                : (formData as any)[field]
            }
            onChange={(e) => handleValidation(field as keyof LabTest, e.target.value)}
          >
            {field === 'gender' && (
              <>
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="both">Both</option>
              </>
            )}
            {field === 'sampleType' && (
              <>
                <option value="">Select</option>
                <option value="blood">Blood</option>
                <option value="urine">Urine</option>
                <option value="saliva">Saliva</option>
                <option value="stool">Stool</option>
                <option value="swab">Swab</option>
              </>
            )}
            {field === 'fasting' && (
              <>
                <option value="">Select</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </>
            )}
          </select>
          {errors[field] && <p className="text-red-500 text-xs mt-1">{errors[field]}</p>}
        </div>
      ))}
    </div>
  );
};

export default BasicInfoSection;
